import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IBasePickerSuggestionsProps, ITag, TagPicker } from '@fluentui/react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as ownFunctions from './SnomedSelector.Functions';

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 300;

const getTextFromItem = (item: ITag) => item.name;

const SnomedSelectorComponent = (props: ownFunctions.Props) => {
    const [t] = useTranslation();

    const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
        suggestionsHeaderText: t('medicalSpecialties.form.suggestionsHeader', { defaultValue: 'Especialidades sugeridas' }),
        noResultsFoundText: t('medicalSpecialties.form.noResultsFound', { defaultValue: 'No se encontró especialidad' })
    };
    const [selectedItems, setSelectedItems] = useState<ITag[]>(
        props.displayText && props.value
            ? [{ key: props.value, name: props.displayText }]
            : []
    );

    const resolveRef = useRef<((tags: ITag[]) => void) | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            props.ClearSnomedSearch();
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (props.displayText && props.value) {
            setSelectedItems([{ key: props.value, name: props.displayText }]);
        } else if (!props.value) {
            setSelectedItems(prev => prev.length === 0 ? prev : []);
        }
    }, [props.displayText, props.value]);

    // Resolve the pending search promise when Redux state updates with results
    useEffect(() => {
        if (!props.isSearching && resolveRef.current) {
            const resolve = resolveRef.current;
            resolveRef.current = null;
            resolve(
                props.searchResults.map((item) => ({
                    key: item.id,
                    name: item.name
                }))
            );
        }
    }, [props.isSearching, props.searchResults]);

    const filterSuggestedTags = useCallback(
        (filterText: string, tagList?: ITag[]): Promise<ITag[]> => {
            if (filterText.trim().length < MIN_SEARCH_LENGTH) {
                return Promise.resolve([]);
            }

            // Cancel pending debounce from previous keystroke
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            // Resolve any pending previous search to avoid dangling promises
            if (resolveRef.current) {
                resolveRef.current([]);
                resolveRef.current = null;
            }

            return new Promise<ITag[]>((resolve) => {
                debounceRef.current = setTimeout(() => {
                    resolveRef.current = resolve;
                    props.SearchSnomedSpecialties(filterText.trim());
                }, DEBOUNCE_MS);
            });
        },
        [props.SearchSnomedSpecialties]
    );

    const handleChange = (items?: ITag[]) => {
        if (items && items.length > 0) {
            setSelectedItems(items);
            const selected = props.searchResults.find(r => r.id === items[0].key);
            if (selected) {
                props.onSelect(selected);
            } else {
                props.onSelect({ id: String(items[0].key), name: items[0].name, description: '', language: '' });
            }
            props.ClearSnomedSearch();
        } else {
            setSelectedItems([]);
            props.onClear();
            props.ClearSnomedSearch();
        }
    };

    return (
        <TagPicker
            selectedItems={selectedItems}
            removeButtonAriaLabel={t('medicalSpecialties.form.clearSnomed', { defaultValue: 'Limpiar' })}
            selectionAriaLabel={t('medicalSpecialties.form.snomedPlaceholder', { defaultValue: 'Buscar código SNOMED (mín. 3 caracteres)' })}
            onResolveSuggestions={filterSuggestedTags}
            getTextFromItem={getTextFromItem}
            pickerSuggestionsProps={pickerSuggestionsProps}
            itemLimit={1}
            onChange={handleChange}
            pickerCalloutProps={{
                doNotLayer: false,
                layerProps: props.layerHostId ? { hostId: props.layerHostId } : undefined
            }}
            inputProps={{
                placeholder: t('medicalSpecialties.form.snomedPlaceholder', { defaultValue: 'Buscar código SNOMED (mín. 3 caracteres)' }),
                autoComplete: 'off'
            }}
        />
    );
};

export default connect(
    ownFunctions.mapStateToProps,
    ownFunctions.mapDispatchToProps
)(SnomedSelectorComponent as any);
